import React from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Avatar,
  Chip,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Warning,
  Flag,
  ExpandMore,
  Error,
  TrendingUp,
  Assessment,
  Analytics,
  ReportProblem,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';
import countries from "../data/countries";

const AidAnalysis = () => {
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();
  // Data for tables
  const monthlyData = [
    { month: t("September 2024"), units: 17012 },
    { month: t("October 2024"), units: 1931490 },
    { month: t("November 2024"), units: 418639 },
    { month: t("December 2024"), units: 207909 },
    { month: t("Undetermined"), units: 231694 },
  ];

  const topDonors = [
    { country: t("UAE"), units: 987822, flag: countries["UAE"]?.flag },
    { country: t("Saudi Arabia"), units: 891133, flag: countries["KSA"]?.flag },
    { country: t("Turkey"), units: 319470, flag: countries["Turkey"]?.flag },
    { country: t("Direct Purchases"), units: 256815, flag: null },
    { country: t("Pakistan"), units: 134304, flag: countries["Pakistan"]?.flag },
  ];

  const aidCategories = [
    { category: t("Medical/Pharmaceutical"), units: 803180 },
    { category: t("Food"), units: 726306 },
    { category: t("Shelter"), units: 564809 },
    { category: t("Health/Hygiene"), units: 304766 },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(135deg, #d32f2f 0%, #f57c00 50%, #1976d2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("Humanitarian Aid Analysis: Data Discrepancies and Distribution Issues")}
        </Typography>
        
        {/* Overview */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#2c3e50' }}>
            {t("Overview")}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', textAlign: 'justify', color: '#34495e' }}>
            {t("analysis_overview_text")}
          </Typography>
        </Paper>

        {/* Critical Warning */}
        <Alert severity="error" sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(211, 47, 47, 0.3)' }}>
          <AlertTitle sx={{ fontWeight: 700, fontSize: '1.2rem' }}>⚠️ {t("Data Reliability Warning")}</AlertTitle>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            {t("analysis_warning_intro")}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, textAlign: 'left' }}>
            <Typography component="li" variant="body1">{t("analysis_issue_1")}</Typography>
            <Typography component="li" variant="body1">{t("analysis_issue_2")}</Typography>
            <Typography component="li" variant="body1">{t("analysis_issue_3")}</Typography>
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'left' }}>
            {t("analysis_confidence_text")}: <Chip label={t("LOW")} color="error" size="small" sx={{ fontWeight: 700 }} /> - {t("analysis_confidence_detail")}
          </Typography>
        </Alert>
      </Box>

      {/* Major Cases of Aid Distribution Discrepancies */}
    
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: 'error.main' }}>
        <ReportProblem sx={{ mr: 2, fontSize: '2rem' }} />
          {t("Major Cases of Aid Distribution Discrepancies")}
        </Typography>
        
        {/* Case 1: Kuwait Aid */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'error.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Error sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 1: Kuwait Aid - Unit Measurement Confusion")}
              </Typography>
              <Chip label={t("Critical")} color="error" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
              {t("The Problem")}:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>{t("Received")}:</strong> {t("42.3 tons from Kuwait (recorded as 42,300 kg = 42,300 units)")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              <strong>{t("Distributed")}:</strong> {t("The 42.3 tons were broken down into:")}
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1">{t("27,405 blankets")}</Typography>
              <Typography component="li" variant="body1">{t("14,880 food rations")}</Typography>
              <Typography component="li" variant="body1">{t("50,975 medical supplies")}</Typography>
              <Typography component="li" variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                <strong>{t("Total distributed: 93,206 units")}</strong>
              </Typography>
            </Box>
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>{t("Critical Issue")}</AlertTitle>
              <Typography variant="body1">
                {t("How can 42,300 units received become 93,206 units distributed? This represents impossible unit conversion.")}
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Case 2: Turkey - Biscuits and Pasta */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Assessment sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 2: Turkey - Biscuits and Pasta Mathematics")}
              </Typography>
              <Chip label={t("High")} color="warning" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
              {t("Biscuits Discrepancy")}:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Typography component="li" variant="body1">
                <strong>{t("HRC (Higher Relief Authority) transferred:")}</strong> {t("5,940 pieces to GEC (Government Emergency Committee)")}
              </Typography>
              <Typography component="li" variant="body1">
                <strong>{t("GEC recorded receiving:")}</strong> {t("720 boxes")}
              </Typography>
              <Typography component="li" variant="body1">
                <strong>{t("Calculation:")}</strong> {t("5,940 ÷ 720 = 8.25 pieces per box")}
              </Typography>
              <Typography component="li" variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                <strong>{t("Problem:")}</strong> {t("How can a box contain 8.25 (eight and a quarter) pieces of biscuits?")}
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
              {t("Pasta Discrepancy")}:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Typography component="li" variant="body1">
                <strong>{t("HRC transferred:")}</strong> {t("10,000 pieces to GEC")}
              </Typography>
              <Typography component="li" variant="body1">
                <strong>{t("GEC recorded receiving:")}</strong> {t("240 boxes")}
              </Typography>
              <Typography component="li" variant="body1">
                <strong>{t("Calculation:")}</strong> {t("10,000 ÷ 240 = 41.67 pieces per box")}
              </Typography>
              <Typography component="li" variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                <strong>{t("Problem:")}</strong> {t("Mathematically illogical ratio suggests either:")}
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">{t("10 boxes disappeared in transit, OR")}</Typography>
                <Typography component="li" variant="body1">{t("Recording error (should be 250 boxes = 40 pieces/box)")}</Typography>
              </Box>
            </Box>
            
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'warning.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("HRC Transferred")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("GEC Received")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Ratio")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Issue")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Biscuits")}</TableCell>
                    <TableCell>{t("5,940 pieces")}</TableCell>
                    <TableCell>{t("720 boxes")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'error.main' }}>{t("8.25 pieces/box")}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{t("Quarter pieces?")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Pasta")}</TableCell>
                    <TableCell>{t("10,000 pieces")}</TableCell>
                    <TableCell>{t("240 boxes")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'error.main' }}>{t("41.67 pieces/box")}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{t("Mathematical impossibility")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Case 3: Turkey - Blankets and Mattresses */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'error.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TrendingUp sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 3: Turkey - Blankets and Mattresses Multiplication")}
              </Typography>
              <Chip label={t("Critical")} color="error" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'error.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Received from Turkey")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Distributed")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Discrepancy")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Blankets")}</TableCell>
                    <TableCell>{t("139,000")}</TableCell>
                    <TableCell>{t("167,000+")}</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{t("+28,000 blankets appeared")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Mattresses")}</TableCell>
                    <TableCell>{t("2,000")}</TableCell>
                    <TableCell>{t("57,000+")}</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{t("+55,000 mattresses appeared")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>{t("Critical Question")}</AlertTitle>
              <Typography variant="body1">
                {t("Where did the additional 28,000 blankets and 55,000 mattresses come from? Were there unreported donations?")}
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Case 4: UAE - Missing Sugar and Food */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Warning sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 4: UAE - Missing Sugar and Food")}
              </Typography>
              <Chip label={t("High")} color="warning" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("Items Received from UAE")}:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Typography component="li" variant="body1">{t("17,280 milk cans")}</Typography>
              <Typography component="li" variant="body1">{t("1,759 bags of sugar (98 tons)")}</Typography>
              <Typography component="li" variant="body1">{t("805 pieces of peas (6 tons)")}</Typography>
              <Typography component="li" variant="body1">{t("200 pieces of lentils (6 tons)")}</Typography>
            </Box>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("Distribution Records (by GEC)")}:
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'warning.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Received")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Distributed")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Missing/Extra")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Milk")}</TableCell>
                    <TableCell>{t("17,280 cans")}</TableCell>
                    <TableCell>{t("360 boxes (17,280 cans if 48/box)")}</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{t("✓ Matches")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Sugar")}</TableCell>
                    <TableCell>{t("1,759 bags (98 tons)")}</TableCell>
                    <TableCell>{t("92 tons")}</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{t("-6 tons missing")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Peas")}</TableCell>
                    <TableCell>{t("805 pieces (6 tons)")}</TableCell>
                    <TableCell>{t("1,185 kg (1.185 tons)")}</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{t("-4.8 tons missing")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Lentils")}</TableCell>
                    <TableCell>{t("200 pieces (6 tons)")}</TableCell>
                    <TableCell>{t("454 kg (7.454 tons)")}</TableCell>
                    <TableCell sx={{ color: 'warning.main', fontWeight: 600 }}>{t("+1 ton extra")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>{t("Critical Issue")}</AlertTitle>
              <Typography variant="body1">
                {t("6 tons of sugar vanished, while 1 ton of lentils appeared from nowhere.")}
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Case 5: Egypt - The Vanishing Blankets */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'info.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Error sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 5: Egypt - The Vanishing Blankets")}
              </Typography>
              <Chip label={t("Medium")} color="info" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                      {t("Received from Egypt")}:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Typography component="li" variant="body1">{t("1,864 food rations")}</Typography>
                      <Typography component="li" variant="body1">{t("4,000 blankets")}</Typography>
                      <Typography component="li" variant="body1">{t("26 tents")}</Typography>
                      <Typography component="li" variant="body1">{t("21,072 kg food materials")}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                      {t("Distributed (according to HRC records)")}:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Typography component="li" variant="body1">{t("1,887 food rations")}</Typography>
                      <Typography component="li" variant="body1" sx={{ color: 'error.main' }}>{t("2,075 blankets")}</Typography>
                      <Typography component="li" variant="body1">{t("24 tents")}</Typography>
                      <Typography component="li" variant="body1" sx={{ color: 'error.main' }}>{t("No food materials recorded")}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>{t("Missing Items")}</AlertTitle>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">{t("~2,000 blankets (half the shipment)")}</Typography>
                <Typography component="li" variant="body1">{t("21 tons of food materials (completely unaccounted for)")}</Typography>
              </Box>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Case 6: Congo - Multiplication Mystery */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'secondary.light', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TrendingUp sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 6: Congo - Multiplication Mystery")}
              </Typography>
              <Chip label={t("High")} color="warning" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("Data")}:
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t("Received")}:</strong> {t("1,050 pieces of used clothing")}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t("Distributed")}:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body1">{t("8,000 blankets")}</Typography>
                <Typography component="li" variant="body1">{t("12,750 pieces of clothing")}</Typography>
              </Box>
            </Box>
            <Alert severity="error">
              <AlertTitle sx={{ fontWeight: 700 }}>{t("Problem")}</AlertTitle>
              <Typography variant="body1">
                {t("How does 1,050 pieces become 20,750 items?")}
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Case 7: Pakistan - The Bulk Disappearance */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'error.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Error sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("Case 7: Pakistan - The Bulk Disappearance")}
              </Typography>
              <Chip label={t("Critical")} color="error" size="small" sx={{ ml: 'auto', mr: 2 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("Received from Pakistan (Total: 125,749 pieces)")}:
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Box component="ul" sx={{ pl: 3 }}>
                  <Typography component="li" variant="body1">{t("5,950 blankets")}</Typography>
                  <Typography component="li" variant="body1">{t("4,000 winter clothes")}</Typography>
                  <Typography component="li" variant="body1">{t("7,124 milk bottles")}</Typography>
                  <Typography component="li" variant="body1">{t("1,845 hygiene bags")}</Typography>
                  <Typography component="li" variant="body1">{t("14,635 relief materials")}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box component="ul" sx={{ pl: 3 }}>
                  <Typography component="li" variant="body1">{t("50,000 medicine pieces")}</Typography>
                  <Typography component="li" variant="body1">{t("2,700 canned food boxes")}</Typography>
                  <Typography component="li" variant="body1">{t("2,000 pieces of clothing")}</Typography>
                  <Typography component="li" variant="body1">{t("22,200 ready-to-eat meals")}</Typography>
                  <Typography component="li" variant="body1">{t("10,295 tents")}</Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
              {t("Distributed (by HRC)")}:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              <Typography component="li" variant="body1">{t("Only 25 blankets")}</Typography>
              <Typography component="li" variant="body1">{t("684 tents")}</Typography>
              <Typography component="li" variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                {t("Everything else: UNACCOUNTED FOR")}
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {t("When traced to GEC records")}:
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'error.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("HRC Received")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("GEC Received")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Issue")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Rice")}</TableCell>
                    <TableCell>{t("Not specified")}</TableCell>
                    <TableCell>{t("168 bags (10 kg each)")}</TableCell>
                    <TableCell sx={{ color: 'warning.main' }}>{t("Unknown origin")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Blankets")}</TableCell>
                    <TableCell>{t("5,950")}</TableCell>
                    <TableCell>{t("1,035")}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{t("Where are 4,915?")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Shelter bags")}</TableCell>
                    <TableCell>{t("Not in HRC list")}</TableCell>
                    <TableCell>{t("2,066")}</TableCell>
                    <TableCell sx={{ color: 'warning.main' }}>{t("New item appeared")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Tents")}</TableCell>
                    <TableCell>{t("10,295")}</TableCell>
                    <TableCell>{t("775")}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{t("Missing 9,520 tents")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Packaged food")}</TableCell>
                    <TableCell>{t("2,700 boxes")}</TableCell>
                    <TableCell>{t("2,250 boxes")}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{t("Missing 450 boxes")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Paper>
      {/* Summary Statistics Dashboard */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Summary Statistics Dashboard")}
        </Typography>
        
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          {t("Total Aid Overview")}
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Total Received by All Agencies")}:</strong> 2,806,744 {t("units")}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Total Distributed")}:</strong> 2,336,729 {t("units")}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1, color: 'error.main', fontWeight: 600 }}>
            <strong>{t("Unaccounted Difference")}:</strong> 470,015 {t("units")} (16.74%)
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Aid by Month (Received)")}
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Month")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units Received")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyData.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.month}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {row.units.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Top Donor Countries/Entities")}
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'success.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Donor")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units Provided")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topDonors.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {row.flag ? (
                        <Avatar src={row.flag} alt={row.country} variant="square" sx={{ width: 32, height: 32, mr: 2,  img: { objectFit: "contain" } }} />
                      ) : (
                        <Avatar variant="square" sx={{ bgcolor: 'primary.main', width: 32, height: 32, mr: 2 }}>
                          <Flag />
                        </Avatar>
                      )}
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {row.country}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {row.units.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Aid Categories Distribution")}
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'info.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Category")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aidCategories.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.category}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {row.units.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Major Cases of Aid Distribution Discrepancies */}
  

      {/* Overall System-Wide Discrepancies */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: 'error.main' }}>
          {t("Overall System-Wide Discrepancies")}
        </Typography>
        
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          {t("Aggregate Comparison")}:
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t("Total Received")}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{t("2,806,744")}</Typography>
                <Typography variant="body1">{t("units (All Agencies)")}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t("Total Distributed")}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{t("2,366,729")}</Typography>
                <Typography variant="body1">{t("units")}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t("Gap")}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{t("470,015")}</Typography>
                <Typography variant="body1">{t("units (16.74%)")}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {t("By Category - Massive Gaps")}:
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'error.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Category")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Received")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Distributed")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Unaccounted")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Food")}</TableCell>
                <TableCell align="right">{t("726,306")}</TableCell>
                <TableCell align="right" sx={{ color: 'warning.main' }}>{t("335,922 (46.2%)")}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{t("390,384 units")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Medicine")}</TableCell>
                <TableCell align="right">{t("803,180")}</TableCell>
                <TableCell align="right">{t("709,358")}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{t("93,822 units")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Shelter")}</TableCell>
                <TableCell align="right">{t("564,809")}</TableCell>
                <TableCell align="right">{t("564,455")}</TableCell>
                <TableCell align="right" sx={{ color: 'warning.main' }}>{t("354 units")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Hygiene")}</TableCell>
                <TableCell align="right">{t("304,766")}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>{t("391,769")}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{t("+86,003 (more distributed!)")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Clothing")}</TableCell>
                <TableCell align="right">{t("22,601")}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>{t("52,841")}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{t("+30,240 (more distributed!)")}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Geographic Distribution Problems */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: 'warning.main' }}>
          {t("Geographic Distribution Problems")}
        </Typography>
        
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t("Unknown Location")}</AlertTitle>
          <Typography variant="body1">
            <strong>{t("1,753,847 units (74% of total) distributed to \"Unknown Location\"")}</strong>
          </Typography>
        </Alert>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Known Distribution by Governorate")}:
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'warning.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Governorate")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units Distributed")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Beirut")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("281,057")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Mount Lebanon")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("226,158")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Bekaa")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("54,145")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Akkar")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("10,626")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("South Lebanon")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("18,171")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Baalbek-Hermel")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t("16,875")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'error.main' }}>{t("Nabatieh")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>{t("50 (lowest)")}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recommendations for System Improvement */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Recommendations for System Improvement")}
        </Typography>
        
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          {t("Immediate Actions Needed")}
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Standardize Measurement Units")}:</strong> {t("standardize_units_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Digital Integration")}:</strong> {t("digital_integration_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Real-time Tracking")}:</strong> {t("realtime_tracking_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Geographic Mapping")}:</strong> {t("geographic_mapping_text")}
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
          {t("Long-term Reforms")}
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 4 }}>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Unified Command Structure")}:</strong> {t("unified_command_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Transparency Portal")}:</strong> {t("transparency_portal_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Independent Auditing")}:</strong> {t("independent_auditing_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Beneficiary Registration")}:</strong> {t("beneficiary_registration_text")}
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
          {t("Critical Issues Identified")}
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Data Quality Problems")}:</strong> {t("data_quality_problems_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Transparency Gaps")}:</strong> {t("transparency_gaps_text")}
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
            <strong>{t("Coordination Failures")}:</strong> {t("coordination_failures_text")}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default AidAnalysis;
